import type { PrintClaim } from '../types'
import { printJobServerMillis } from './printJobTime'

/** How long a print claim blocks a `missing` retry while the job record is still unwritten. */
export const PRINT_CLAIM_TTL_MS = 120_000

function claimTimeMillis(claim: PrintClaim): number | null {
  const serverMs = printJobServerMillis(claim.serverAt)
  if (serverMs !== null) return serverMs
  if (!claim.at) return null
  const at = Date.parse(claim.at)
  return Number.isFinite(at) ? at : null
}

export function isLivePrintClaim(
  claim: PrintClaim | undefined,
  serverNowMs: number,
  ttlMs: number = PRINT_CLAIM_TTL_MS,
): boolean {
  if (!claim) return false
  const at = claimTimeMillis(claim)
  if (at === null) return false
  const age = serverNowMs - at
  if (age < 0) return true
  return age < ttlMs
}

/**
 * Whether this send may print. Live note + same `claimId` is only Firestore
 * retrying this write. A new send (new `claimId`), including this tablet’s
 * extra check, loses.
 */
export function decidePrintClaimWin(
  remote: PrintClaim | undefined,
  next: PrintClaim,
  serverNowMs: number,
): boolean {
  if (!isLivePrintClaim(remote, serverNowMs)) return true
  const remoteId = remote?.claimId?.trim()
  const nextId = next.claimId?.trim()
  return Boolean(remoteId && nextId && remoteId === nextId)
}

export function liveClaimForSlot(
  claims: Record<string, PrintClaim> | undefined,
  slotKey: string,
  serverNowMs: number,
  ttlMs: number = PRINT_CLAIM_TTL_MS,
): PrintClaim | undefined {
  const claim = claims?.[slotKey]
  return isLivePrintClaim(claim, serverNowMs, ttlMs) ? claim : undefined
}
